// ProfileSceneDOM_Template.js - Complete Profile Management with Template Implementation
// Integrates desktop-profile-management.html + mobile-profile-management.html templates

import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db } from "../firebaseConfig.js";
import { createSidebarNavigation } from "../components/SharedSidebarNavigation.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";
import companyLookupService from "../utils/CompanyLookupService.js";
import { baseSceneIntegration } from "../services/BaseSceneIntegration.js";
import { addCriticalNavigationCSS } from "../utils/CriticalNavigationCSS.js";

export default class ProfileSceneDOMTemplate extends BaseMenuScene {
    constructor() {
        super({ key: "ProfileSceneDOMTemplate" });
        this.domElement = null;
        this.currentTab = 'account';
        this.isMobile = window.MobileDetectionService?.isMobile() || window.innerWidth <= 768;
        this.userProfile = null;
    }

    async create() {
        console.log('👤 ProfileSceneDOMTemplate: Creating complete profile management with templates');
        
        // Load user data
        await this.loadUserProfile();
        
        // Create the profile interface
        this.createProfileInterface();
        
        // Initialize with enterprise services (replaces duplicate event binding)
        baseSceneIntegration.initializeTemplateScene(this, {
            overlayId: 'auth-menu-dom-overlay',
            bindEvents: true,
            enableNavigation: true
        });
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Make this scene available globally for email change handling
        window.profileScene = this;
        
        this.sceneReady = true;
    }

    async loadUserProfile() {
        const user = auth.currentUser;
        if (user) {
            // Load basic user data
            this.userProfile = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || user.email.split('@')[0],
                emailVerified: user.emailVerified,
                photoURL: user.photoURL
            };
            
            // Load company verification status from Firestore
            try {
                const profileDoc = await db.collection('userProfiles').doc(user.uid).get();
                if (profileDoc.exists) {
                    const profileData = profileDoc.data();
                    this.userProfile = {
                        ...this.userProfile,
                        companyEmailVerified: profileData.companyEmailVerified || false,
                        companyEmail: profileData.companyEmail || '',
                        firmDomain: profileData.firmDomain || '',
                        firmAccess: profileData.firmAccess || false,
                        companyEmailVerifiedAt: profileData.companyEmailVerifiedAt,
                        // Other profile fields
                        bio: profileData.bio || '',
                        title: profileData.title || '',
                        company: profileData.company || '',
                        location: profileData.location || ''
                    };
                    
                    console.log('👤 ProfileSceneDOM: Company verification status loaded:', {
                        verified: this.userProfile.companyEmailVerified,
                        email: this.userProfile.companyEmail,
                        domain: this.userProfile.firmDomain
                    });
                }
            } catch (error) {
                console.error('👤 Error loading profile data from Firestore:', error);
            }
        }
        console.log('👤 ProfileSceneDOM: User profile loaded:', this.userProfile);
    }

    createProfileInterface() {
        // CRITICAL: Add global critical CSS for all navigation
        addCriticalNavigationCSS();
        
        // Remove existing overlay  
        const existingOverlay = document.getElementById('auth-menu-dom-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }
        
        console.log('👤 Creating EXACT profile template interface with shared layout...');
        
        // Detect mobile vs desktop for correct template
        const isMobile = window.innerWidth <= 768;
        console.log(`👤 Profile template: ${isMobile ? 'Mobile' : 'Desktop'} (${window.innerWidth}px)`);

        // Load EXACT same CSS as Dashboard and add Profile content styling
        this.loadProfileCSS();

        const overlay = document.createElement('div');
        overlay.id = 'auth-menu-dom-overlay';  // SAME ID as Dashboard
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
            box-sizing: border-box;
        `;

        // Load Profile CSS (already called in create method)

        if (this.isMobile) {
            overlay.innerHTML = this.createMobileProfileHTML();
        } else {
            overlay.innerHTML = this.createDesktopProfileHTML();
        }

        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        // Register with overlay manager for proper cleanup
        templateOverlayManager.registerOverlay('auth-menu-dom-overlay', 'ProfileSceneDOMTemplate');
    }

    loadProfileCSS() {
        console.log('🎨 Loading Profile CSS...');
        
        // Load EXACT same CSS as Dashboard
        if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
            const link = document.createElement('link');
            link.id = 'desktop-executive-dashboard-exact-css';
            link.rel = 'stylesheet';
            link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter
            document.head.appendChild(link);
        }
        
        // Add Profile content styling
        this.addProfileContentCSS();
        
        console.log('🎨 All Profile CSS loaded');
    }

    addProfileContentCSS() {
        const contentStyle = document.createElement('style');
        contentStyle.id = 'profile-content-css';
        contentStyle.textContent = `
            /* Profile Content Styling - EXACT Template Structure */
            
            /* Header styling */
            #auth-menu-dom-overlay .header {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(10px) !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
                padding: 0 32px !important;
                height: 70px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                margin: -32px -32px 32px -32px !important;
            }
            
            /* Profile Card - EXACT template structure */
            #auth-menu-dom-overlay .profile-card {
                background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%) !important;
                border: 1px solid rgba(0, 212, 255, 0.2) !important;
                border-radius: 24px !important;
                padding: 32px !important;
                margin-bottom: 32px !important;
                display: grid !important;
                grid-template-columns: auto 1fr auto !important;
                gap: 32px !important;
                align-items: center !important;
            }
            
            #auth-menu-dom-overlay .avatar-large {
                width: 96px !important;
                height: 96px !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                border-radius: 50% !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                font-size: 36px !important;
                font-weight: 700 !important;
                color: #0f172a !important;
                position: relative !important;
            }
            
            /* Tabs Container */
            #auth-menu-dom-overlay .tabs-container {
                display: flex !important;
                gap: 8px !important;
                margin-bottom: 32px !important;
                background: rgba(255, 255, 255, 0.05) !important;
                border-radius: 12px !important;
                padding: 4px !important;
            }
            
            #auth-menu-dom-overlay .tab-button {
                padding: 12px 24px !important;
                background: transparent !important;
                border: none !important;
                border-radius: 8px !important;
                color: var(--text-secondary) !important;
                font-weight: 600 !important;
                font-size: 14px !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
            }
            
            #auth-menu-dom-overlay .tab-button.active {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                color: #0f172a !important;
            }
            
            /* Form Grid */
            #auth-menu-dom-overlay .form-grid {
                display: grid !important;
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 32px !important;
            }
            
            #auth-menu-dom-overlay .form-section {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 20px !important;
                padding: 24px !important;
            }
        `;
        document.head.appendChild(contentStyle);
        console.log('🎨 Profile content CSS added');
    }

    // REMOVED: 37 lines of duplicate navigation logic
    // Now handled by baseSceneIntegration.initializeTemplateScene()
    // Navigation actions are centralized in NavigationActionHandler service


    createDesktopProfileHTML() {
        const initials = this.getInitials(this.userProfile.displayName);
        
        return `
            <!-- Animated Background (SAME as Dashboard) -->
            <div class="background-effects">
                <div class="bg-grid"></div>
                <div class="bg-glow bg-glow-1"></div>
                <div class="bg-glow bg-glow-2"></div>
            </div>
            
            <!-- Layout Container (SAME as Dashboard) -->
            <div class="app-container">
                <!-- Sidebar Navigation (EXACT Dashboard structure) -->
                <aside class="sidebar">
                    <div class="sidebar-header">
                        <div class="logo">
                            <div class="logo-icon">D</div>
                            <div class="logo-text">
                                <div>DeckOps.ai</div>
                                <div class="logo-subtitle">Executive Platform</div>
                            </div>
                        </div>
                    </div>
                    
                    <nav class="nav-menu">
                        <div class="nav-section">
                            <div class="nav-section-title">Main</div>
                            <a href="#" class="nav-item" data-action="dashboard">
                                <span class="nav-icon">🏠</span>
                                <span class="nav-label">Dashboard</span>
                            </a>
                            <a href="#" class="nav-item" data-action="analytics">
                                <span class="nav-icon">📊</span>
                                <span class="nav-label">Analytics</span>
                            </a>
                            <a href="#" class="nav-item" data-action="ppt-hero-analytics">
                                <span class="nav-icon">📈</span>
                                <span class="nav-label">PPT Hero Analytics</span>
                                <span class="nav-badge">Detailed</span>
                            </a>
                            <a href="#" class="nav-item" data-action="hall-of-fame">
                                <span class="nav-icon">🏆</span>
                                <span class="nav-label">Hall of Fame</span>
                                <span class="nav-badge">New</span>
                            </a>
                        </div>
                    
                        <div class="nav-section">
                            <div class="nav-section-title">Training Modules</div>
                            <a href="#" class="nav-item" data-action="game-center">
                                <span class="nav-icon">🎮</span>
                                <span class="nav-label">Game Center</span>
                            </a>
                            <a href="#" class="nav-item" data-game="ppt-hero">
                                <span class="nav-icon">📈</span>
                                <span class="nav-label">PPT Hero</span>
                            </a>
                            <a href="#" class="nav-item" data-game="philosophy-flow">
                                <span class="nav-icon">🎨</span>
                                <span class="nav-label">Philosophy Flow</span>
                            </a>
                            <a href="#" class="nav-item" data-game="slide-wars">
                                <span class="nav-icon">⚔️</span>
                                <span class="nav-label">Slide Wars</span>
                            </a>
                            <a href="#" class="nav-item" data-game="classify-tech">
                                <span class="nav-icon">🏛️</span>
                                <span class="nav-label">Classify Tech</span>
                            </a>
                        </div>
                        
                        <div class="nav-section">
                            <div class="nav-section-title">Settings</div>
                            <a href="#" class="nav-item active" data-action="profile">
                                <span class="nav-icon">⚙️</span>
                                <span class="nav-label">Preferences</span>
                            </a>
                            <a href="#" class="nav-item" data-action="help">
                                <span class="nav-icon">❓</span>
                                <span class="nav-label">Help & Support</span>
                            </a>
                        </div>
                    </nav>
                    
                    <div class="sidebar-footer">
                        <div class="user-profile">
                            <div class="user-avatar">${initials}</div>
                            <div class="user-info">
                                <div class="user-name">${this.userProfile.displayName}</div>
                                <div class="user-role">Executive</div>
                            </div>
                            <span style="color: var(--text-muted);">›</span>
                        </div>
                    </div>
                </aside>
                
                <!-- Main Content -->
                <main class="main-content">
                    <!-- Header with breadcrumb -->
                    <header class="header">
                        <div class="header-left">
                            <h1 class="page-title">Profile Management</h1>
                            <div class="breadcrumb">
                                <span>Home</span>
                                <span class="breadcrumb-separator">/</span>
                                <span>Profile</span>
                            </div>
                        </div>
                        <div class="header-right">
                            <button class="save-btn" data-action="save">Save Changes</button>
                        </div>
                    </header>
                    
                    <!-- Profile Card -->
                    <div class="profile-card">
                        <div class="avatar-large">
                            ${initials}
                            <div class="avatar-edit-overlay">
                                <span>📷</span>
                            </div>
                        </div>
                        <div class="profile-info">
                            <h2 class="profile-name">${this.userProfile.displayName}</h2>
                            <p class="profile-email">${this.userProfile.email}</p>
                            <div class="profile-badges">
                                <span class="badge verified">✓ Verified</span>
                                <span class="badge premium">Premium Member</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Settings Sections -->
                    <div class="settings-grid">
                        <!-- Account Settings -->
                        <div class="settings-card">
                            <div class="card-header">
                                <h3 class="card-title">Account Settings</h3>
                                <span class="card-icon">⚙️</span>
                            </div>
                            <div class="card-content">
                                ${this.createAccountSettingsForm()}
                            </div>
                        </div>
                        
                        <!-- Security Settings -->
                        <div class="settings-card">
                            <div class="card-header">
                                <h3 class="card-title">Security & Privacy</h3>
                                <span class="card-icon">🔒</span>
                            </div>
                            <div class="card-content">
                                ${this.createSecuritySettingsForm()}
                            </div>
                        </div>
                        
                        <!-- Preferences -->
                        <div class="settings-card">
                            <div class="card-header">
                                <h3 class="card-title">Preferences</h3>
                                <span class="card-icon">🎨</span>
                            </div>
                            <div class="card-content">
                                ${this.createPreferencesForm()}
                            </div>
                        </div>
                        
                        <!-- Notifications -->
                        <div class="settings-card">
                            <div class="card-header">
                                <h3 class="card-title">Notifications</h3>
                                <span class="card-icon">🔔</span>
                            </div>
                            <div class="card-content">
                                ${this.createNotificationSettingsForm()}
                            </div>
                        </div>
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="action-buttons">
                        <button class="btn-secondary" data-action="cancel">Cancel Changes</button>
                        <button class="btn-primary" data-action="save">Save All Changes</button>
                    </div>
                </main>
            </div>
        `;
    }

    createMobileProfileHTML() {
        const initials = this.getInitials(this.userProfile.displayName);
        
        return `
            ${createTemplateHeader('Profile Management', ['Home', 'Dashboard', 'Profile'])}
            
            <!-- Mobile Profile Management Template Structure -->
            <header class="header">
                <button class="back-button" data-action="back">
                    <span>←</span>
                </button>
                <h1 class="header-title">Profile</h1>
                <button class="save-button" data-action="save">Save</button>
            </header>
            
            <div class="content">
                <!-- Avatar Section -->
                <section class="avatar-section">
                    <div class="avatar">
                        ${initials}
                        <div class="avatar-edit">📷</div>
                    </div>
                    <h2 class="user-name">${this.userProfile.displayName}</h2>
                    <p class="user-role">Executive</p>
                    <p class="user-company">Bain & Company</p>
                </section>
                
                <!-- Tabs -->
                <div class="tabs">
                    <button class="tab active" data-tab="account">Account</button>
                    <button class="tab" data-tab="security">Security</button>
                    <button class="tab" data-tab="preferences">Preferences</button>
                </div>
                
                <!-- Tab Content -->
                <div class="tab-content">
                    <div class="tab-panel active" data-panel="account">
                        ${this.createMobileAccountForm()}
                    </div>
                    <div class="tab-panel" data-panel="security">
                        ${this.createMobileSecurityForm()}
                    </div>
                    <div class="tab-panel" data-panel="preferences">
                        ${this.createMobilePreferencesForm()}
                    </div>
                </div>
            </div>
        `;
    }

    createAccountSettingsForm() {
        return `
            <div class="form-group">
                <label class="form-label">Display Name</label>
                <input type="text" class="form-input" value="${this.userProfile.displayName}" data-field="displayName">
            </div>
            <div class="form-group">
                <label class="form-label">Email Address</label>
                <input type="email" class="form-input" value="${this.userProfile.email}" data-field="email" readonly>
                <small class="form-help">Email cannot be changed</small>
            </div>
            <div class="form-group">
                <label class="form-label">Job Title</label>
                <input type="text" class="form-input" placeholder="Your job title" data-field="jobTitle">
            </div>
            <div class="form-group">
                <label class="form-label">Company</label>
                <input type="text" class="form-input" placeholder="Your company" data-field="company">
            </div>
        `;
    }

    createSecuritySettingsForm() {
        const currentVerification = this.userProfile.companyEmailVerified || false;
        const currentCompanyEmail = this.userProfile.companyEmail || '';
        const currentCompanyDomain = this.userProfile.firmDomain || '';
        
        return `
            <!-- Company Email Verification Section -->
            <div class="form-group company-verification-section">
                <label class="form-label">
                    <span>Company Email Verification</span>
                    ${currentVerification ? 
                        `<span class="verification-badge verified">✅ Verified</span>` : 
                        `<span class="verification-badge unverified">⚠️ Not Verified</span>`
                    }
                </label>
                <div class="company-verification-content">
                    ${currentVerification ? `
                        <div class="verified-company-info">
                            <div class="company-email-display">
                                <strong>${currentCompanyEmail}</strong>
                                <span class="company-domain">(${currentCompanyDomain})</span>
                            </div>
                            <div class="verification-actions">
                                <button type="button" class="btn-secondary" data-action="change-company">Change Company</button>
                                <button type="button" class="btn-outline" data-action="remove-verification">Remove Verification</button>
                            </div>
                        </div>
                    ` : `
                        <div class="unverified-company-section">
                            <div class="form-group">
                                <label class="form-label">Company Email</label>
                                <input type="email" class="form-input" placeholder="Enter your company email (e.g., you@company.com)" data-field="companyEmail" value="${currentCompanyEmail}" onchange="window.profileScene?.handleEmailChange?.(this.value)">
                            </div>
                            
                            <div class="form-group" id="company-selection-group" style="display: none;">
                                <label class="form-label">Detected Company</label>
                                <select class="form-input" data-field="detectedCompany" id="company-dropdown">
                                    <option value="">Select your company...</option>
                                    ${this.generateCompanyOptions()}
                                </select>
                                <div class="company-match-status" id="company-match-status"></div>
                            </div>
                            
                            <div class="company-verification-help">
                                <p class="help-text">Verify your company email to unlock firm-specific features and leaderboards.</p>
                                <p class="help-text small">Supported companies: Bain & Company, McKinsey, BCG, Deloitte, and more.</p>
                                <div class="email-company-validation" id="email-company-validation"></div>
                            </div>
                            
                            <button type="button" class="btn-primary verification-btn" data-action="verify-company-email" ${!currentCompanyEmail ? 'disabled' : ''} id="verify-email-btn">
                                <span class="btn-icon">📧</span>
                                <span class="btn-text">Send Verification Email</span>
                            </button>
                        </div>
                    `}
                </div>
            </div>
            
            <!-- Password Security Section -->
            <div class="form-group">
                <label class="form-label">Current Password</label>
                <input type="password" class="form-input" placeholder="Enter current password" data-field="currentPassword">
            </div>
            <div class="form-group">
                <label class="form-label">New Password</label>
                <input type="password" class="form-input" placeholder="Enter new password" data-field="newPassword">
            </div>
            <div class="form-group">
                <label class="form-label">Confirm Password</label>
                <input type="password" class="form-input" placeholder="Confirm new password" data-field="confirmPassword">
            </div>
            <div class="form-group">
                <label class="toggle-label">
                    <input type="checkbox" class="toggle-input" data-field="twoFactorAuth">
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">Enable Two-Factor Authentication</span>
                </label>
            </div>
            
            <style>
                .company-verification-section {
                    border: 1px solid rgba(0, 212, 255, 0.2);
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 24px;
                    background: rgba(0, 212, 255, 0.05);
                }
                
                .verification-badge {
                    font-size: 12px;
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-weight: 600;
                    margin-left: 8px;
                }
                
                .verification-badge.verified {
                    background: rgba(16, 185, 129, 0.2);
                    color: #10b981;
                }
                
                .verification-badge.unverified {
                    background: rgba(245, 158, 11, 0.2);
                    color: #f59e0b;
                }
                
                .verified-company-info {
                    padding: 16px;
                    background: rgba(16, 185, 129, 0.1);
                    border-radius: 8px;
                    margin-top: 12px;
                }
                
                .company-email-display {
                    margin-bottom: 12px;
                }
                
                .company-domain {
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 14px;
                    margin-left: 8px;
                }
                
                .verification-actions {
                    display: flex;
                    gap: 12px;
                }
                
                .unverified-company-section {
                    margin-top: 12px;
                }
                
                .company-verification-help {
                    margin: 12px 0;
                }
                
                .help-text {
                    font-size: 13px;
                    color: rgba(255, 255, 255, 0.7);
                    margin: 4px 0;
                }
                
                .help-text.small {
                    font-size: 12px;
                    color: rgba(255, 255, 255, 0.5);
                }
                
                .btn-outline {
                    padding: 8px 16px;
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: rgba(255, 255, 255, 0.8);
                    border-radius: 6px;
                    font-size: 12px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                
                .btn-outline:hover {
                    border-color: rgba(255, 255, 255, 0.5);
                    color: rgba(255, 255, 255, 1);
                }
                
                .verification-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 20px;
                    background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                    border: none;
                    border-radius: 8px;
                    color: #0f172a;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    margin-top: 12px;
                }
                
                .verification-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 24px rgba(0, 212, 255, 0.3);
                }
                
                .verification-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }
                
                .btn-icon {
                    font-size: 16px;
                }
                
                .btn-text {
                    font-weight: 600;
                }
            </style>
        `;
    }

    createPreferencesForm() {
        return `
            <div class="form-group">
                <label class="form-label">Language</label>
                <select class="form-select" data-field="language">
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                </select>
            </div>
            <div class="form-group">
                <label class="form-label">Timezone</label>
                <select class="form-select" data-field="timezone">
                    <option value="UTC">UTC</option>
                    <option value="EST">Eastern Time</option>
                    <option value="PST">Pacific Time</option>
                </select>
            </div>
            <div class="form-group">
                <label class="toggle-label">
                    <input type="checkbox" class="toggle-input" data-field="darkMode" checked>
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">Dark Mode</span>
                </label>
            </div>
        `;
    }

    createNotificationSettingsForm() {
        return `
            <div class="form-group">
                <label class="toggle-label">
                    <input type="checkbox" class="toggle-input" data-field="emailNotifications" checked>
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">Email Notifications</span>
                </label>
            </div>
            <div class="form-group">
                <label class="toggle-label">
                    <input type="checkbox" class="toggle-input" data-field="pushNotifications" checked>
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">Push Notifications</span>
                </label>
            </div>
            <div class="form-group">
                <label class="toggle-label">
                    <input type="checkbox" class="toggle-input" data-field="performanceAlerts" checked>
                    <span class="toggle-slider"></span>
                    <span class="toggle-text">Performance Alerts</span>
                </label>
            </div>
        `;
    }

    createMobileAccountForm() {
        return `
            <div class="form-section">
                <h3 class="section-title">Personal Information</h3>
                <div class="form-group">
                    <label class="form-label">Display Name</label>
                    <input type="text" class="form-input" value="${this.userProfile.displayName}" data-field="displayName">
                </div>
                <div class="form-group">
                    <label class="form-label">Email</label>
                    <input type="email" class="form-input" value="${this.userProfile.email}" readonly>
                </div>
                <div class="form-group">
                    <label class="form-label">Job Title</label>
                    <input type="text" class="form-input" placeholder="Your job title" data-field="jobTitle">
                </div>
            </div>
        `;
    }

    createMobileSecurityForm() {
        return `
            <div class="form-section">
                <h3 class="section-title">Password & Security</h3>
                <div class="form-group">
                    <label class="form-label">Current Password</label>
                    <input type="password" class="form-input" data-field="currentPassword">
                </div>
                <div class="form-group">
                    <label class="form-label">New Password</label>
                    <input type="password" class="form-input" data-field="newPassword">
                </div>
                <div class="security-options">
                    <div class="option-item">
                        <span class="option-icon">🔐</span>
                        <div class="option-content">
                            <h4 class="option-title">Two-Factor Authentication</h4>
                            <p class="option-description">Add extra security to your account</p>
                        </div>
                        <label class="toggle">
                            <input type="checkbox" data-field="twoFactorAuth">
                            <span class="toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </div>
        `;
    }

    createMobilePreferencesForm() {
        return `
            <div class="form-section">
                <h3 class="section-title">App Preferences</h3>
                <div class="preference-options">
                    <div class="option-item">
                        <span class="option-icon">🌙</span>
                        <div class="option-content">
                            <h4 class="option-title">Dark Mode</h4>
                            <p class="option-description">Use dark theme</p>
                        </div>
                        <label class="toggle">
                            <input type="checkbox" checked data-field="darkMode">
                            <span class="toggle-slider"></span>
                        </label>
                    </div>
                    <div class="option-item">
                        <span class="option-icon">🔔</span>
                        <div class="option-content">
                            <h4 class="option-title">Push Notifications</h4>
                            <p class="option-description">Receive performance alerts</p>
                        </div>
                        <label class="toggle">
                            <input type="checkbox" checked data-field="pushNotifications">
                            <span class="toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </div>
        `;
    }

    setupEventListeners() {
        // NOTE: Navigation events are handled by bindExactDashboardEvents() - no duplicate binding here

        // Tab switching (mobile)
        this.domElement.querySelectorAll('[data-tab]').forEach(tab => {
            tab.addEventListener('click', (e) => {
                e.preventDefault();
                this.switchTab(tab.dataset.tab);
            });
        });

        // Form field changes
        this.domElement.querySelectorAll('[data-field]').forEach(field => {
            field.addEventListener('change', (e) => {
                this.handleFieldChange(field.dataset.field, e.target.value || e.target.checked);
            });
        });
    }

    // Removed conflicting handleAction method - using handleTemplateAction instead
    
    // Keep Profile-specific actions
    saveProfile() {
        console.log('👤 Saving profile...');
        // Implementation for saving profile
    }
    
    cancelChanges() {
        console.log('👤 Canceling changes...');
        // Implementation for canceling changes
    }

    switchTab(tabName) {
        // Update tab appearance
        this.domElement.querySelectorAll('.tab').forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tab === tabName);
        });

        // Update panel visibility
        this.domElement.querySelectorAll('.tab-panel').forEach(panel => {
            panel.classList.toggle('active', panel.dataset.panel === tabName);
        });

        this.currentTab = tabName;
    }

    handleFieldChange(fieldName, value) {
        console.log('👤 Profile field changed:', fieldName, value);
        // Store changes for saving later
        if (!this.pendingChanges) {
            this.pendingChanges = {};
        }
        this.pendingChanges[fieldName] = value;
    }

    async saveProfile() {
        console.log('👤 Saving profile changes:', this.pendingChanges);
        
        try {
            // Implement actual save logic here
            // Update Firebase user profile
            // Update Firestore user document
            
            this.showNotification('Profile updated successfully!', 'success');
            this.pendingChanges = {};
        } catch (error) {
            console.error('Error saving profile:', error);
            this.showNotification('Error saving profile. Please try again.', 'error');
        }
    }

    cancelChanges() {
        this.pendingChanges = {};
        this.createProfileInterface(); // Refresh to original values
    }

    async openAnalytics() {
        // Integrate with existing analytics system
        if (window.uiUpgradeManager && window.uiUpgradeManager.isNewUIEnabled) {
            // Use new analytics template
            this.startScene('AnalyticsSceneDOM');
        } else {
            // Use existing analytics
            const { populateAnalyticsOverlay } = await import("../ui/analyticsUI.js");
            await populateAnalyticsOverlay(auth.currentUser.uid, "PPTHero");
            window.UIController.showOverlay('analytics-overlay');
        }
    }

    async openHallOfFame() {
        console.log('🏆 Opening new desktop Hall of Fame template...');
        this.startScene('HallOfFameDesktopTemplate');
    }

    async handleLogout() {
        try {
            await auth.signOut();
            this.startScene('GuestMenuSceneDOM');
        } catch (error) {
            console.error('Logout error:', error);
            this.showNotification('Error logging out. Please try again.', 'error');
        }
    }

    getInitials(name) {
        return name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';
    }

    forceCleanupBeforeTransition() {
        console.log('👤 ProfileSceneDOM: Force cleanup before transition...');
        
        // Use overlay manager for proper cleanup
        templateOverlayManager.prepareForTransition('AuthenticatedMenuSceneDOM');
    }

    async initiateCompanyEmailVerification() {
        console.log('🏢 ProfileSceneDOM: Initiating company email verification...');
        
        const companyEmailInput = this.domElement.querySelector('[data-field="companyEmail"]');
        const companyEmail = companyEmailInput?.value?.trim();
        
        if (!companyEmail) {
            this.showNotification('Please enter your company email address.', 'warning');
            return;
        }
        
        try {
            // Import and use the existing firmAuthService
            const { initiateCompanyEmailVerification } = await import('../services/firmAuthService.js');
            
            this.showNotification('Sending verification email...', 'info');
            
            const result = await initiateCompanyEmailVerification(companyEmail);
            
            if (result.success) {
                this.showNotification(result.message, 'success');
                // Refresh the profile to show updated state
                setTimeout(() => {
                    this.loadUserProfile();
                    this.refreshProfileInterface();
                }, 2000);
            } else {
                this.showNotification(result.message, 'error');
            }
        } catch (error) {
            console.error('🏢 Error initiating company verification:', error);
            this.showNotification('Failed to send verification email. Please try again.', 'error');
        }
    }
    
    async changeCompany() {
        console.log('🏢 ProfileSceneDOM: Changing company...');
        
        // Show confirmation dialog
        if (window.confirm('Are you sure you want to change your company? This will remove your current verification and firm-specific features.')) {
            try {
                // Remove current verification
                await this.removeCompanyVerification();
                
                // Show company input
                this.showCompanyChangeInterface();
                
            } catch (error) {
                console.error('🏢 Error changing company:', error);
                this.showNotification('Failed to change company. Please try again.', 'error');
            }
        }
    }
    
    async removeCompanyVerification() {
        console.log('🏢 ProfileSceneDOM: Removing company verification...');
        
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) {
                this.showNotification('User not authenticated.', 'error');
                return;
            }
            
            // Update user profile to remove verification
            await db.collection('userProfiles').doc(userId).update({
                companyEmailVerified: false,
                companyEmail: '',
                firmDomain: '',
                firmAccess: false,
                companyEmailVerifiedAt: null
            });
            
            this.showNotification('Company verification removed successfully.', 'success');
            
            // Refresh the profile interface
            setTimeout(() => {
                this.loadUserProfile();
                this.refreshProfileInterface();
            }, 1000);
            
        } catch (error) {
            console.error('🏢 Error removing company verification:', error);
            this.showNotification('Failed to remove verification. Please try again.', 'error');
        }
    }
    
    showCompanyChangeInterface() {
        // Update the verification section to show input for new company
        const verificationSection = this.domElement.querySelector('.company-verification-section');
        if (verificationSection) {
            verificationSection.innerHTML = `
                <label class="form-label">
                    <span>Company Email Verification</span>
                    <span class="verification-badge unverified">⚠️ Not Verified</span>
                </label>
                <div class="company-verification-content">
                    <div class="unverified-company-section">
                        <input type="email" class="form-input" placeholder="Enter your new company email" data-field="companyEmail">
                        <div class="company-verification-help">
                            <p class="help-text">Enter your new company email to verify with a different organization.</p>
                            <p class="help-text small">Supported companies: Bain & Company, McKinsey, BCG, Deloitte, and more.</p>
                        </div>
                        <button type="button" class="btn-primary" data-action="verify-company-email">
                            Send Verification Email
                        </button>
                    </div>
                </div>
            `;
            
            // Re-bind event listeners for the new buttons
            this.setupEventListeners();
        }
    }
    
    generateCompanyOptions() {
        const companies = companyLookupService.getSupportedCompanies();
        return companies.map(company => 
            `<option value="${company.name}" data-domain="${company.domain}">${company.name} (${company.domain})</option>`
        ).join('');
    }
    
    handleEmailChange(email) {
        console.log('🏢 ProfileSceneDOM: Email changed to:', email);
        
        if (!email) {
            this.hideCompanySelection();
            return;
        }
        
        // Auto-detect company from email
        const suggestion = companyLookupService.suggestCompanyFromEmail(email);
        
        if (suggestion.suggested) {
            this.showCompanySelection(suggestion.company);
            this.showValidationMessage(suggestion.message, 'success');
        } else {
            this.hideCompanySelection();
            this.showValidationMessage(suggestion.message, 'warning');
        }
        
        // Enable/disable verification button
        const verifyBtn = document.getElementById('verify-email-btn');
        if (verifyBtn) {
            verifyBtn.disabled = !suggestion.suggested;
        }
    }
    
    showCompanySelection(companyInfo) {
        const selectionGroup = document.getElementById('company-selection-group');
        const dropdown = document.getElementById('company-dropdown');
        const statusDiv = document.getElementById('company-match-status');
        
        if (selectionGroup && dropdown && statusDiv) {
            selectionGroup.style.display = 'block';
            dropdown.value = companyInfo.name;
            statusDiv.innerHTML = `
                <div class="company-match-success">
                    <span class="match-icon">✅</span>
                    <span class="match-text">Email domain matches ${companyInfo.name}</span>
                </div>
            `;
        }
    }
    
    hideCompanySelection() {
        const selectionGroup = document.getElementById('company-selection-group');
        if (selectionGroup) {
            selectionGroup.style.display = 'none';
        }
    }
    
    showValidationMessage(message, type) {
        const validationDiv = document.getElementById('email-company-validation');
        if (validationDiv) {
            const className = type === 'success' ? 'validation-success' : 'validation-warning';
            validationDiv.innerHTML = `
                <div class="${className}">
                    <span class="validation-icon">${type === 'success' ? '✅' : '⚠️'}</span>
                    <span class="validation-text">${message}</span>
                </div>
            `;
        }
    }

    refreshProfileInterface() {
        // Refresh the entire profile interface to reflect changes
        console.log('👤 ProfileSceneDOM: Refreshing profile interface...');
        
        // Remove current overlay and recreate
        if (this.domElement) {
            this.domElement.remove();
        }
        
        // Recreate the profile interface with updated data
        this.createProfileInterface();
        this.setupEventListeners();
        
        // Re-register global reference
        window.profileScene = this;
    }

    showNotification(message, type = 'info') {
        // ENTERPRISE UPGRADE: Use ToastManager for professional notifications
        console.log(`👤 Notification (${type}):`, message);
        
        if (window.toastManager) {
            window.toastManager.show(type, 'Profile Update', message, 4000);
        } else if (window.showGeneralMessage) {
            window.showGeneralMessage(message, 3000, type === 'error');
        }
    }

    shutdown() {
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        // Clean up profile scene overlay
        const profileOverlay = document.getElementById('profile-scene-overlay');
        if (profileOverlay) {
            profileOverlay.remove();
        }
        
        super.shutdown();
    }
}
